import { LOCATION_INITIALIZED } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
} from "@angular/core";
import { MAT_DIALOG_DEFAULT_OPTIONS } from "@angular/material/dialog";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { provideMomentDateAdapter } from "@angular/material-moment-adapter";
import { bootstrapApplication, BrowserModule } from "@angular/platform-browser";

import { ELocale } from "@core/enums/elocale.enum";
import { Language } from "@core/enums/language";
import { GlobalErrorHandler } from "@core/handlers/global-error.handler";
import { PaginatorHelper } from "@core/helpers/paginator-helper";
import { HttpActivityInterceptor } from "@core/interceptors/http-activity.interceptor";
import { RegisterInterceptor } from "@core/interceptors/register.interceptor";
import { registerAppLocales } from "@core/register-locales";
import { AuthService } from "@core/services/auth.service";
import { environment } from "@env/environment";
import { AuthEffects } from "@features/auth/store/auth.effects";
import { ClientsAndAccountsEffects } from "@features/clients-and-accounts/store/clients-and-accounts.effects";
import { CompositionModelEffects } from "@features/composition-model-management/store/composition-model.effects";
import { CorporateActionsEffects } from "@features/corporate-actions/store/corporate-actions.effects";
import { DashboardEffects } from "@features/dashboard/store/dashboard.effects";
import { ManagementEffects } from "@features/management/store/management.effects";
import { MessageEffects } from "@features/messages/store/message.effects";
import { ProductEffects } from "@features/products/store/product.effects";
import { ReportsEffects } from "@features/reports-panel/store/reports.effects";
import { SystemTimeEffects } from "@features/system-time/store/system-time.effects";
import { TradingEffects } from "@features/trading/store/trading.effects";
import { TransactionEffects } from "@features/transactions/store/transaction.effects";
import { EffectsModule } from "@ngrx/effects";
import { StoreModule } from "@ngrx/store";
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from "@ngx-translate/core";
import { NgxCleaveDirectiveModule } from "ngx-cleave-directive";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { MultiTranslateHttpLoader } from "ngx-translate-multi-http-loader";
import { lastValueFrom } from "rxjs";

import { ApiModule as AuthApiModule } from "./app/api/auth";
import { authApiConfigurationFactory } from "./app/api/auth-api.config";
import { AppComponent } from "./app/app.component";
import { AppRoutingModule } from "./app/app-routing.module";
import * as fromApp from "./app/store/app.reducer";

if (environment.production) {
  enableProdMode();
}

registerAppLocales();

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      NgxSkeletonLoaderModule.forRoot({
        theme: {
          extendsFromRoot: true,
          // Was "5vh". Every loading placeholder in the application - 61 files
          // of them - was sized from the viewport HEIGHT: 34px in a short
          // window, 54px on a 1080p monitor, and collapsing in between. That
          // is the same bug as the 5vh form-field height, and it is why the
          // loading state looked wrong at some window sizes and not others.
          // 2.25rem is a table row, which is what most of them stand in for.
          height: "2.25rem",
          // Without a gap, consecutive placeholders fuse into one grey slab.
          margin: "0 0 0.5rem",
          padding: 0,
          "border-radius": "0.375rem",
        },
      }),
      BrowserModule,
      AppRoutingModule,
      MatSnackBarModule,
      NgxCleaveDirectiveModule,
      AuthApiModule.forRoot(authApiConfigurationFactory),
      StoreModule.forRoot(fromApp.APP_REDUCER),
      EffectsModule.forRoot([
        AuthEffects,
        DashboardEffects,
        ManagementEffects,
        TradingEffects,
        ProductEffects,
        MessageEffects,
        ClientsAndAccountsEffects,
        TransactionEffects,
        ReportsEffects,
        CorporateActionsEffects,
        CompositionModelEffects,
        SystemTimeEffects,
      ]),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: (handler: HttpBackend): MultiTranslateHttpLoader => {
            return new MultiTranslateHttpLoader(handler, [
              {
                prefix: "./assets/i18n/features/accountmanager/",
                suffix: ".json",
              },
              { prefix: "./assets/i18n/features/advisor/", suffix: ".json" },
              { prefix: "./assets/i18n/features/auth/", suffix: ".json" },
              {
                prefix: "./assets/i18n/features/bankbatches/",
                suffix: ".json",
              },
              {
                prefix: "./assets/i18n/features/business-assistant/",
                suffix: ".json",
              },
              { prefix: "./assets/i18n/features/client/", suffix: ".json" },
              {
                prefix: "./assets/i18n/features/collectionmanagement/",
                suffix: ".json",
              },
              { prefix: "./assets/i18n/features/common/", suffix: ".json" },
              {
                prefix: "./assets/i18n/features/communication/",
                suffix: ".json",
              },
              { prefix: "./assets/i18n/features/dashboard/", suffix: ".json" },
              { prefix: "./assets/i18n/features/dividend/", suffix: ".json" },
              { prefix: "./assets/i18n/features/rebalance/", suffix: ".json" },
              {
                prefix: "./assets/i18n/features/disbursement/",
                suffix: ".json",
              },
              {
                prefix: "./assets/i18n/features/financialinstitute/",
                suffix: ".json",
              },
              { prefix: "./assets/i18n/features/funds/", suffix: ".json" },
              { prefix: "./assets/i18n/features/insurer/", suffix: ".json" },
              {
                prefix: "./assets/i18n/features/intermediary/",
                suffix: ".json",
              },
              { prefix: "./assets/i18n/features/messages/", suffix: ".json" },
              {
                prefix: "./assets/i18n/features/productmanagement/",
                suffix: ".json",
              },
              {
                prefix: "./assets/i18n/features/composition-management/",
                suffix: ".json",
              },
              { prefix: "./assets/i18n/features/report/", suffix: ".json" },
              { prefix: "./assets/i18n/features/shared/", suffix: ".json" },
              { prefix: "./assets/i18n/features/trading/", suffix: ".json" },
              {
                prefix: "./assets/i18n/features/clients-and-accounts/",
                suffix: ".json",
              },
              { prefix: "./assets/i18n/features/notfound/", suffix: ".json" },
            ]);
          },
          deps: [HttpBackend],
        },
      })
    ),
    provideAppInitializer(async (): Promise<void> => {
      const translate = inject(TranslateService);
      await inject(LOCATION_INITIALIZED, { optional: true });
      // this language will be used as a fallback when a translation
      translate.setDefaultLang("nl");
      try {
        // Honour the language picked on the login screen (or in the sidenav)
        // so a reload does not fall back to the default language.
        const storedLanguage = localStorage.getItem(ELocale.localStorageKey);
        const language =
          storedLanguage &&
          Object.values<string>(Language).includes(storedLanguage)
            ? storedLanguage
            : translate.defaultLang;
        await lastValueFrom(translate.use(language));
      } catch (err) {
        console.error(err);
      }
    }),
    // Attempt a silent refresh before routing starts, so a valid session (HttpOnly refresh
    // cookie) survives a full page reload even though the access token lives only in memory.
    provideAppInitializer(
      (): Promise<void> => inject(AuthService).bootstrapSession()
    ),
    // The formats go through provideMomentDateAdapter: it registers its own
    // MAT_DATE_FORMATS (moment's "L", MM/DD/YYYY in English), and as the later
    // provider it replaced a separate MAT_DATE_FORMATS entry here.
    provideMomentDateAdapter({
      parse: {
        dateInput: ["DD-MM-YYYY", "D-M-YYYY", "DD/MM/YYYY", "D/M/YYYY"],
      },
      display: {
        dateInput: "DD-MM-YYYY",
        monthYearLabel: "MMM YYYY",
        dateA11yLabel: "LL",
        monthYearA11yLabel: "MMMM YYYY",
      },
    }),
    {
      provide: LOCALE_ID,
      useValue: "nl-NL",
    },
    // First, so it wraps the auth interceptor and also counts a request
    // that is replayed after a token refresh.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpActivityInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RegisterInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: MatPaginatorIntl,
      useClass: PaginatorHelper,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        autoFocus: "dialog",
        restoreFocus: true,
        disableClose: true,
        // Every dialog in the application gets the shell's dialog layout:
        // title and actions pinned, the content the only thing that scrolls.
        // Without it Material caps mat-dialog-content at 65vh inside a panel
        // that is also capped, which is how a dialog ends up with two
        // scrollbars and an Approve button you have to scroll to reach.
        panelClass: "talero-dialog",
        // 57 call sites open with a fixed desktop width, up to 85rem. Material
        // caps at 80vw, which wastes four gutters on a small screen; 92vw
        // gives the content the room and changes nothing on a large screen,
        // where the requested width is under the cap anyway.
        maxWidth: "92vw",
        // 88vh read as "stuck to the edges" on a tall screen and cost a
        // scrollbar on a short one. 92vh still shows the page around the
        // dialog while giving a twelve-field form the rows it needs.
        maxHeight: "92vh",
      },
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
}).catch((err) => console.error(err));
